/**
 * AttendanceSettingsTab — Phase-3a Settings surface.
 *
 * Two sections:
 *   1. **Correction window** — how far back (days) HR/admin can retro-
 *      actively edit attendance without hitting a Post-Payroll flag.
 *      Standard-plan feature. Backend enforces the range 1-90.
 *   2. **Leave types** — CRUD for the leave categories that show up on
 *      the leave-request dialog + Employee-Master balance tracker.
 *      Carry-forward / encashment toggles are stored for every plan;
 *      the actual math kicks in only when the Enterprise Workforce
 *      Suite add-on is active (badge in the UI hints at that).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAddons } from "@/contexts/AddonsContext";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, Clock, Umbrella, Warning, X } from "@phosphor-icons/react";

const COLOR_SWATCHES = [
  "#4A6753", "#6E8FA6", "#A78F5A", "#B7796B",
  "#7C6BB7", "#4B7A8A", "#8A6BB7", "#6B8A7A",
];

const emptyForm = () => ({
  name: "",
  code: "",
  days_per_year: 12,
  carry_forward: false,
  encashment: false,
  is_paid: true,
  color: COLOR_SWATCHES[0],
  active: true,
});


export default function AttendanceSettingsTab() {
  const { hasEnterpriseWorkforce } = useAddons();

  // Correction window state
  const [correctionWindow, setCorrectionWindow] = useState(7);
  const [savingWindow, setSavingWindow] = useState(false);
  // Leave types state
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [savingType, setSavingType] = useState(false);

  const load = async () => {
    setLoadingTypes(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/settings/attendance"),
        api.get("/settings/leave-types"),
      ]);
      setCorrectionWindow(s.data?.correction_window_days ?? 7);
      setTypes(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingTypes(false); }
  };
  useEffect(() => { load(); }, []);

  const saveWindow = async () => {
    const days = Number(correctionWindow);
    if (isNaN(days) || days < 1 || days > 90) {
      toast.error("Correction window must be between 1 and 90 days");
      return;
    }
    setSavingWindow(true);
    try {
      await api.put("/settings/attendance", { correction_window_days: days });
      toast.success(`Correction window set to ${days} days`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingWindow(false); }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      code: row.code || "",
      days_per_year: row.days_per_year ?? 0,
      carry_forward: !!row.carry_forward,
      encashment: !!row.encashment,
      is_paid: row.is_paid !== false,
      color: row.color || COLOR_SWATCHES[0],
      active: row.active !== false,
    });
    setDialogOpen(true);
  };

  const saveType = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSavingType(true);
    try {
      const payload = {
        ...form,
        days_per_year: Number(form.days_per_year) || 0,
      };
      if (editing) {
        await api.put(`/settings/leave-types/${editing.id}`, payload);
        toast.success("Leave type updated");
      } else {
        await api.post("/settings/leave-types", payload);
        toast.success("Leave type added");
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingType(false); }
  };

  const removeType = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"? Existing leave rows referencing it will still show the name.`)) return;
    try {
      await api.delete(`/settings/leave-types/${row.id}`);
      toast.success("Leave type deactivated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-settings-tab">
      {/* ============ Correction window ============ */}
      <Card data-testid="correction-window-card">
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <Clock size={16} weight="duotone" /> Attendance correction window
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            How far back HR/admin can edit staff attendance without triggering the
            Post-Payroll correction workflow. Default: 7 days. Max: 90 days.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5">
              <Label>Editable window (days)</Label>
              <Input
                type="number" min={1} max={90}
                value={correctionWindow}
                onChange={e => setCorrectionWindow(e.target.value)}
                className="w-32"
                data-testid="correction-window-input"
              />
            </div>
            <Button onClick={saveWindow} disabled={savingWindow} data-testid="save-correction-window">
              {savingWindow ? "Saving…" : "Save window"}
            </Button>
            <div className="text-[11px] text-muted-foreground pb-2">
              Any edit older than this window is flagged; if a payroll has been generated for that period,
              it triggers a Post-Payroll Correction review.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============ Leave types ============ */}
      <Card data-testid="leave-types-card">
        <CardHeader>
          <div className="flex flex-row justify-between items-center gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
                <Umbrella size={16} weight="duotone" /> Leave types ({types.length})
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Categories staff can request. Carry-forward and encashment only take effect while the{" "}
                <span className="font-medium">Enterprise Workforce Suite</span> add-on is active.
              </p>
            </div>
            <Button onClick={openAdd} className="gap-1.5" data-testid="add-leave-type">
              <Plus size={14} weight="bold" /> New leave type
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loadingTypes ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : types.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No leave types yet. Add Casual / Sick / Earned Leave to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Days/year</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>
                      Carry forward
                      {!hasEnterpriseWorkforce && <Warning size={12} className="inline ml-1 text-amber-600" />}
                    </TableHead>
                    <TableHead>
                      Encashment
                      {!hasEnterpriseWorkforce && <Warning size={12} className="inline ml-1 text-amber-600" />}
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {types.map(row => (
                    <TableRow key={row.id} data-testid={`leave-type-row-${row.id}`} className={row.active === false ? "opacity-60" : ""}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: row.color || COLOR_SWATCHES[0] }} />
                          <span className="text-sm font-medium">{row.name}</span>
                          <code className="text-[10px] text-muted-foreground">{row.code}</code>
                          {row.active === false && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.days_per_year}</TableCell>
                      <TableCell>
                        {row.is_paid !== false
                          ? <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">Paid</Badge>
                          : <Badge className="text-[10px] bg-red-100 text-red-800 border-red-200">Unpaid</Badge>}
                      </TableCell>
                      <TableCell>
                        {row.carry_forward
                          ? <Badge className="text-[10px] bg-primary/10 text-primary border-primary/30">Yes</Badge>
                          : <span className="text-[11px] text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {row.encashment
                          ? <Badge className="text-[10px] bg-primary/10 text-primary border-primary/30">Yes</Badge>
                          : <span className="text-[11px] text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="gap-1 h-7 px-2 text-xs" data-testid={`leave-type-edit-${row.id}`}>
                          <PencilSimple size={12} /> Edit
                        </Button>
                        {row.active !== false && (
                          <Button size="sm" variant="ghost" onClick={() => removeType(row)} className="gap-1 h-7 px-2 text-xs text-red-700" data-testid={`leave-type-del-${row.id}`}>
                            <Trash size={12} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leave type dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent className="sm:max-w-lg bg-card" data-testid="leave-type-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit leave type" : "New leave type"}</DialogTitle>
            <DialogDescription className="text-xs">
              Defines a leave category (Casual, Sick, Earned…) with a yearly entitlement and pay behaviour.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Casual Leave"
                data-testid="leave-type-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Code <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
                <Input
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                  placeholder="Auto from name"
                  data-testid="leave-type-code"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Days per year</Label>
                <Input
                  type="number" min={0} max={365} step="0.5"
                  value={form.days_per_year}
                  onChange={e => setForm(f => ({ ...f, days_per_year: e.target.value }))}
                  data-testid="leave-type-days"
                />
              </div>
            </div>

            <div className="rounded-md border border-border p-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.is_paid}
                  onCheckedChange={v => setForm(f => ({ ...f, is_paid: !!v }))}
                  data-testid="leave-type-paid"
                />
                <span className="text-sm">Paid leave <span className="text-[10px] text-muted-foreground">— unpaid leave feeds payslip deduction</span></span>
              </label>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Carry forward unused days</div>
                  <div className="text-[11px] text-muted-foreground">Unused balance rolls into next period.</div>
                </div>
                <Switch
                  checked={form.carry_forward}
                  disabled={!hasEnterpriseWorkforce}
                  onCheckedChange={v => setForm(f => ({ ...f, carry_forward: !!v }))}
                  data-testid="leave-type-carry"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Encashment at period end</div>
                  <div className="text-[11px] text-muted-foreground">Pay out unused days when the period closes.</div>
                </div>
                <Switch
                  checked={form.encashment}
                  disabled={!hasEnterpriseWorkforce}
                  onCheckedChange={v => setForm(f => ({ ...f, encashment: !!v }))}
                  data-testid="leave-type-encash"
                />
              </div>

              {!hasEnterpriseWorkforce && (
                <div className="text-[10px] rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-2 py-1.5 flex items-start gap-1.5">
                  <Warning size={11} className="mt-0.5" />
                  <span>Carry-forward and encashment settings are stored but only enforced while the Enterprise Workforce Suite add-on is active.</span>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Colour</Label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_SWATCHES.map(c => (
                  <button
                    key={c} type="button"
                    onClick={() => setForm(f => ({ ...f, color: c }))}
                    className={`w-7 h-7 rounded-full border-2 transition ${form.color === c ? "border-foreground scale-110" : "border-transparent hover:scale-105"}`}
                    style={{ background: c }}
                    aria-label={`Pick colour ${c}`}
                    data-testid={`leave-type-color-${c}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }} className="gap-1" data-testid="leave-type-cancel">
              <X size={13} /> Cancel
            </Button>
            <Button onClick={saveType} disabled={savingType} data-testid="leave-type-save">
              {savingType ? "Saving…" : editing ? "Save changes" : "Add leave type"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
